import { useCallback, useEffect, useMemo, useState } from 'react';
import { getDailyLog, toDateKey } from '../services/dailyLog';
import { getSessionsForDateRange } from '../services/sessions';
import { getAllTasks } from '../services/tasks';
import { getAllDayFocus } from '../services/dayFocus';
import type { DailyLog, DayFocus, Session, TaskPlan } from '../types';

export interface JournalDayData {
  dateKey: string;
  sessions: Session[];
  dayFocuses: DayFocus[];
  tasks: TaskPlan[];
  dailyLog: DailyLog | null;
}

export function useJournalDay(userId: string | null, date: Date) {
  const [data, setData] = useState<JournalDayData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dateKey = useMemo(() => toDateKey(date), [date]);

  const load = useCallback(async () => {
    if (!userId) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(dayStart);
      dayEnd.setDate(dayEnd.getDate() + 1);

      const [sessions, dailyLog, allTasks, allDayFocuses] = await Promise.all([
        getSessionsForDateRange(userId, dayStart, dayEnd),
        getDailyLog(userId, dateKey),
        getAllTasks(userId),
        getAllDayFocus(userId),
      ]);

      const tasks = allTasks.filter((task) => isSameDay(getTaskDate(task), dayStart));
      const dayFocuses = allDayFocuses.filter((focus) => focus.focusDate === dateKey);
      const sortedSessions = [...sessions].sort((a, b) => getSessionDate(a).getTime() - getSessionDate(b).getTime());

      setData({
        dateKey,
        sessions: sortedSessions,
        dayFocuses,
        tasks,
        dailyLog,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load journal day');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [userId, date, dateKey]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, refetch: load };
}

function getSessionDate(session: Session): Date {
  if (session.startedAt?.toDate) return session.startedAt.toDate();
  return new Date((session.startedAt as unknown as { seconds: number }).seconds * 1000);
}

function getTaskDate(task: TaskPlan): Date {
  if (task.createdAt?.toDate) return task.createdAt.toDate();
  return new Date((task.createdAt as unknown as { seconds: number }).seconds * 1000);
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
